import React from 'react';
import {
  Box, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { Check } from 'lucide-react-native';

export type FlowStepStatus = 'done' | 'active' | 'upcoming';

export type FlowStepDescriptor = {
  key: string;
  label: string;
  subtitle?: string;
  status: FlowStepStatus;
};

export type VerticalFlowStepperDensity = 'comfortable' | 'compact';

export type VerticalFlowStepperProps = {
  steps: FlowStepDescriptor[];
  testID?: string;
  density?: VerticalFlowStepperDensity;
};

const MARKER_SIZE: Record<VerticalFlowStepperDensity, number> = {
  comfortable: 48,
  compact: 40,
};

const ROW_PY = {
  comfortable: '$6',
  compact: '$4',
} as const satisfies Record<VerticalFlowStepperDensity, string>;

/** Navy accent (#344054) — matches mockup active marker/bar and $neutral700. */
const NAVY = '$neutral700';

/**
 * Generic left-rail stepper for oral-exam flow surfaces (lobby-style briefing screens).
 */
export function VerticalFlowStepper({
  steps,
  testID,
  density = 'comfortable',
}: VerticalFlowStepperProps) {
  const markerSize = MARKER_SIZE[density];
  const rowPy = ROW_PY[density];
  const upcomingNumberSize = density === 'compact' ? 'sm' : 'md';
  const activeNumberSize = density === 'compact' ? 'md' : 'lg';

  return (
    <VStack
      testID={testID}
      bg="$bgCard"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$lg"
      overflow="hidden"
    >
      {steps.map((step, index) => {
        const isActive = step.status === 'active';
        const isDone = step.status === 'done';
        const isLast = index === steps.length - 1;

        return (
          <HStack
            key={step.key}
            testID={`${testID ?? 'flow-stepper'}-step-${step.key}`}
            alignItems="stretch"
            bg={isActive ? '$bgBrandSubtle' : 'transparent'}
          >
            <Box
              w={5}
              alignSelf="stretch"
              bg={isActive ? NAVY : 'transparent'}
              testID={isActive ? `${testID ?? 'flow-stepper'}-active-bar` : undefined}
            />
            <HStack
              flex={1}
              alignItems="flex-start"
              space="md"
              pl="$6"
              pr="$7"
              py={rowPy}
            >
              <VStack alignItems="center" width={markerSize} flexShrink={0}>
                <Box
                  w={markerSize}
                  h={markerSize}
                  borderRadius="$full"
                  alignItems="center"
                  justifyContent="center"
                  borderWidth={isActive ? 0 : 2}
                  borderColor="$borderMedium"
                  bg={isActive ? NAVY : '$bgCard'}
                  zIndex={1}
                >
                  {isDone ? (
                    <Icon as={Check} size="sm" color="$statusSuccess" />
                  ) : (
                    <Text
                      size={isActive ? activeNumberSize : upcomingNumberSize}
                      fontWeight="bold"
                      color={isActive ? '$textInverted' : '$textBody'}
                    >
                      {index + 1}
                    </Text>
                  )}
                </Box>
                {!isLast && (
                  <Box
                    flex={1}
                    width={2}
                    bg="$borderMedium"
                    testID={index === 0 ? 'flow-stepper-connector' : undefined}
                  />
                )}
              </VStack>

              <VStack flex={1} space="xs" minHeight={markerSize} justifyContent="center">
                <Text
                  size="md"
                  fontWeight={isActive ? 'semibold' : 'normal'}
                  color={isActive ? '$textHeading' : isDone ? '$textBody' : '$textSubtle'}
                >
                  {step.label}
                </Text>
                {!!step.subtitle && (
                  <Text size="sm" color="$textSubtle">
                    {step.subtitle}
                  </Text>
                )}
              </VStack>
            </HStack>
          </HStack>
        );
      })}
    </VStack>
  );
}
